/**
 * BackendStatusBanner — Axe 1 audit S++.
 *
 * Polle GET /api/health toutes les 10s. Affiche un bandeau collant en haut
 * dès que le backend est down ou injoignable. Cache toute UI normale derrière
 * un état explicite : on ne veut PAS qu'un opérateur regarde des données stale
 * en pensant qu'elles sont fraîches (cf finding F-01).
 *
 * États possibles :
 *   - ok        : backend répond 200 → bandeau caché
 *   - warming   : 1ʳᵉ requête en cours → rien (évite le flash)
 *   - degraded  : 1 échec → bandeau orange « connexion instable »
 *   - down      : 3 échecs consécutifs → bandeau rouge bloquant
 *
 * Boutons :
 *   - Reconnecter   : force une tentative immédiate (degraded + down)
 *   - Redémarrer    : POST /api/admin/restart, puis poll jusqu'au retour (degraded only)
 */

import { type CSSProperties, useCallback, useEffect, useRef, useState } from 'react';

type Status = 'warming' | 'ok' | 'degraded' | 'down';
const POLL_MS = 10_000;
const FAIL_THRESHOLD_DOWN = 3;

export function BackendStatusBanner() {
  const [status, setStatus]           = useState<Status>('warming');
  const [lastCheck, setLastCheck]     = useState<Date | null>(null);
  const [restarting, setRestarting]   = useState(false);
  const failCount  = useRef<number>(0);
  const timerRef   = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);

  const runCheck = useCallback(async () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    try {
      const res = await fetch('/api/health', {
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (cancelledRef.current) return;
      if (res.ok) {
        failCount.current = 0;
        setStatus('ok');
        setRestarting(false);
      } else {
        failCount.current += 1;
        setStatus(failCount.current >= FAIL_THRESHOLD_DOWN ? 'down' : 'degraded');
      }
    } catch {
      if (cancelledRef.current) return;
      failCount.current += 1;
      setStatus(failCount.current >= FAIL_THRESHOLD_DOWN ? 'down' : 'degraded');
    } finally {
      setLastCheck(new Date());
      if (!cancelledRef.current) timerRef.current = setTimeout(runCheck, POLL_MS);
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    runCheck();
    return () => {
      cancelledRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [runCheck]);

  const handleReconnect = () => runCheck();

  const handleRestart = async () => {
    setRestarting(true);
    try {
      await fetch('/api/admin/restart', { method: 'POST', cache: 'no-store' });
    } catch {
      // Normal si le serveur meurt avant de répondre
    }
    // Poll agressif jusqu'au retour du serveur (max 30s)
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await fetch('/api/health', { cache: 'no-store' });
        if (res.ok) { runCheck(); return; }
      } catch { /* still down */ }
      if (++attempts < 30) setTimeout(poll, 1000);
      else setRestarting(false);
    };
    setTimeout(poll, 800);
  };

  if (status === 'ok' || status === 'warming') return null;

  const isDown   = status === 'down';
  const bg       = isDown ? '#7f1d1d' : '#92400e';
  const accent   = isDown ? '#fecaca' : '#fde68a';
  const label    = isDown ? 'BACKEND INDISPONIBLE' : 'CONNEXION INSTABLE';
  const detail   = isDown
    ? "Aucune communication avec l'API. Les données affichées sont obsolètes (cache local). Toute action est désactivée tant que la connexion n'est pas rétablie."
    : "Le backend ne répond pas correctement. Nouvelle tentative dans quelques secondes. Les données peuvent être obsolètes.";

  const btnStyle: CSSProperties = {
    padding: '3px 10px',
    borderRadius: 4,
    border: `1px solid ${accent}`,
    background: 'transparent',
    color: accent,
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    letterSpacing: '0.04em',
    flexShrink: 0,
    opacity: restarting ? 0.5 : 1,
  };

  return (
    <div
      role="alert"
      aria-live={isDown ? 'assertive' : 'polite'}
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        background: bg,
        color: '#fff',
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        fontSize: 13,
        borderBottom: `2px solid ${accent}`,
        boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: accent,
          animation: 'lab-pulse 1.4s ease-in-out infinite',
          flexShrink: 0,
        }}
      />
      <strong style={{ fontSize: 11, letterSpacing: '0.08em', fontWeight: 700 }}>
        {label}
      </strong>
      <span style={{ opacity: 0.92 }}>
        {restarting ? 'Redémarrage en cours, nouvelle tentative de connexion…' : detail}
      </span>

      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <span style={{ fontSize: 11, opacity: 0.7, fontFamily: 'var(--font-mono, monospace)' }}>
          Dernière tentative : {lastCheck ? lastCheck.toLocaleTimeString('fr-FR') : '—'} · échecs : {failCount.current}
        </span>
        <button
          style={btnStyle}
          disabled={restarting}
          onClick={handleReconnect}
          title="Tenter une reconnexion immédiate"
        >
          Reconnecter
        </button>
        {!isDown && (
          <button
            style={btnStyle}
            disabled={restarting}
            onClick={handleRestart}
            title="Demander au backend de redémarrer son process"
          >
            {restarting ? 'Redémarrage…' : 'Redémarrer le service'}
          </button>
        )}
      </div>

      <style>{`
        @keyframes lab-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.5; transform: scale(1.4); }
        }
      `}</style>
    </div>
  );
}
